// components/analytics/components/charts/TrendLineChart.tsx
import { Line, LineOptions } from '@antv/g2plot';
import { Annotation } from '@antv/g2plot/lib/types/annotation';
import { useEffect, useRef, useState } from 'react';
import { Skeleton, Alert } from 'antd';

interface TrendLineChartProps {
  data: Array<{
    date: string;
    value: number;
    type?: string;
    [key: string]: any;
  }>;
  xField: string;
  yField: string;
  seriesField?: string;
  height?: number;
  smooth?: boolean;
  scatterPlot?: boolean;
  confidenceField?: string;
  loading?: boolean;
}

export const TrendLineChart: React.FC<TrendLineChartProps> = ({
  data,
  xField,
  yField,
  seriesField,
  height = 300,
  smooth = true,
  scatterPlot = false,
  confidenceField,
  loading = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Line | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current || !data?.length) {
      return;
    }

    if (chartRef.current) {
      try {
        chartRef.current.destroy();
        chartRef.current = null;
      } catch (error) {
        console.error('[TrendLineChart] Destroy error:', error);
      }
    }

    try {
      setChartError(null);

      const config: LineOptions = {
        data,
        xField,
        yField,
        smooth,
        xAxis: {
          type: 'time',
          tickCount: 5,
          label: {
            autoRotate: true,
            autoHide: false,
            autoEllipsis: true
          }
        },
        yAxis: {
          label: {
            formatter: (v: string) => `${v}`
          },
          grid: {
            line: {
              style: {
                lineDash: [4, 4]
              }
            }
          }
        },
        tooltip: {
          showCrosshairs: !scatterPlot,
          shared: true,
          showMarkers: !scatterPlot
        },
        animation: {
          appear: {
            animation: 'fade-in',
            duration: 1000
          }
        },
        point: scatterPlot ? {
          size: 5,
          shape: 'circle',
          style: {
            fill: '#1890ff',
            stroke: '#fff',
            lineWidth: 2
          }
        } : undefined,
        area: !scatterPlot ? {} : undefined,
        ...(seriesField ? {
          seriesField,
          color: ['#1890ff', '#2fc25b', '#facc14', '#f04864'],
          legend: {
            position: 'top-right',
            marker: {
              symbol: 'circle'
            }
          }
        } : {}),
        ...(confidenceField ? {
          annotations: [{
            type: 'regionFilter',
            start: ['min', 'min'],
            end: ['max', 'max'],
            color: '#1890ff',
            style: {
              opacity: 0.1
            }
          } as Annotation],
          tooltip: {
            formatter: (datum: any) => ({
              name: datum.date,
              value: datum.value,
              confidence: datum[confidenceField]
            })
          }
        } : {})
      };

      const chart = new Line(containerRef.current, config);
      chartRef.current = chart;
      chart.render();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      console.error('[TrendLineChart] Initialization error:', error);
      setChartError(`Error al inicializar el gráfico: ${errorMessage}`);
    }

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
          chartRef.current = null;
        } catch (error) {
          console.error('[TrendLineChart] Cleanup error:', error);
        }
      }
    };
  }, [data, xField, yField, smooth, scatterPlot, seriesField, confidenceField]);

  useEffect(() => {
    const handleResize = (): void => {
      if (chartRef.current && containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        chartRef.current.changeSize(width, height);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <div style={{ height, padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (chartError) {
    return (
      <div style={{ height, padding: '16px' }}>
        <Alert
          message="Error en el gráfico"
          description={chartError}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div style={{ height, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
        <div style={{ fontSize: '16px', marginBottom: '8px' }}>Sin datos de tendencias</div>
        <div style={{ fontSize: '12px' }}>No hay datos disponibles para el período seleccionado</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        height,
        width: '100%',
        position: 'relative'
      }}
      data-testid="trend-line-chart"
    />
  );
};
